'use client';

import React, { useState, useEffect, useRef } from 'react';

const ApertureMark = ({ size = 22, color = '#1F4D3F' }: { size?: number; color?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" width={size} height={size} aria-hidden="true">
    <g transform="rotate(0 12 12)"><path d="M 12 5 Q 13.2 9 12 11" /></g>
    <g transform="rotate(60 12 12)"><path d="M 12 5 Q 13.2 9 12 11" /></g>
    <g transform="rotate(120 12 12)"><path d="M 12 5 Q 13.2 9 12 11" /></g>
    <g transform="rotate(180 12 12)"><path d="M 12 5 Q 13.2 9 12 11" /></g>
    <g transform="rotate(240 12 12)"><path d="M 12 5 Q 13.2 9 12 11" /></g>
    <g transform="rotate(300 12 12)"><path d="M 12 5 Q 13.2 9 12 11" /></g>
  </svg>
);

const NODES = [
  { id: 'sales', label: 'Sales', angle: 0, insight: 'Sales ↑ 18%', sub: 'Q2 vs Q1 · All outlets', color: '#1F4D3F' },
  { id: 'inventory', label: 'Inventory', angle: 60, insight: 'Inventory Risk ↓', sub: '7 SKUs need reorder', color: '#2D6A5A' },
  { id: 'finance', label: 'Finance', angle: 120, insight: 'Margin ↑ 4.2%', sub: 'Net margin 11.6%', color: '#3A7D6B' },
  { id: 'customers', label: 'Customers', angle: 180, insight: 'Retention ↑ 78%', sub: '218 contacts · repeat', color: '#1F4D3F' },
  { id: 'purchasing', label: 'Purchasing', angle: 240, insight: '14 POs confirmed', sub: 'Sunrise Distributors', color: '#2D6A5A' },
  { id: 'operations', label: 'Operations', angle: 300, insight: '3 outlets active', sub: 'AMD · SRT · VDR', color: '#3A7D6B' },
];

const NODE_DESCRIPTIONS: Record<string, string> = {
  sales: 'vina keeps an eye on your sales data and alerts you when it finds problems, unusual changes, or new opportunities.',
  inventory: 'vina tracks your inventory and quickly points out low stock, unusual changes, and areas where you can improve.',
  finance: 'vina tracks your finances and highlights cash flow issues, unusual changes, and areas to improve before they affect your business.',
  customers: 'vina keeps track of your customer data and highlights unusual activity, customer issues, and opportunities to improve relationships.',
  purchasing: 'vina tracks your purchasing activity and highlights unusual spending, supplier issues, and opportunities to save costs.',
  operations: 'vina keeps track of your daily operations and highlights process issues, unusual activity, and areas where you can improve efficiency.',
};

function NodeComponent({
  node,
  cx,
  cy,
  active,
  onHover,
  onLeave,
  pulse,
}: {
  node: typeof NODES[0];
  cx: number;
  cy: number;
  active: boolean;
  onHover: () => void;
  onLeave: () => void;
  pulse: boolean;
}) {
  return (
    <g
      style={{ cursor: 'pointer' }}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      role="button"
      aria-label={`${node.label}: ${node.insight}`}
      tabIndex={0}
      onFocus={onHover}
      onBlur={onLeave}
    >
      {/* Pulse ring */}
      {pulse && (
        <circle cx={cx} cy={cy} r="28" fill="none" stroke={node.color} strokeWidth="1"
          style={{ opacity: 0.3, animation: 'nodeRing 2s ease-out infinite' }} />
      )}
      {/* Node circle */}
      <circle
        cx={cx} cy={cy} r="22"
        fill={active ? node.color : 'rgba(255,255,255,0.85)'}
        stroke={active ? node.color : 'rgba(31,77,63,0.2)'}
        strokeWidth={active ? 0 : 1.5}
        style={{ transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)', filter: active ? 'drop-shadow(0 4px 12px rgba(31,77,63,0.3))' : 'none' }}
      />
      {/* Label */}
      <text
        x={cx} y={cy + 36}
        textAnchor="middle"
        fill={active ? '#1F4D3F' : '#4F665F'}
        fontSize="11"
        fontFamily="var(--font-mono)"
        letterSpacing="0.04em"
        style={{ transition: 'fill 0.3s ease' }}
      >
        {node.label}
      </text>
      {/* Node label inside */}
      <text
        x={cx} y={cy + 4}
        textAnchor="middle"
        fill={active ? '#FFFFFF' : node.color}
        fontSize="9"
        fontFamily="var(--font-mono)"
        letterSpacing="0.03em"
        style={{ transition: 'fill 0.3s ease' }}
      >
        {node.label.slice(0, 3).toUpperCase()}
      </text>
    </g>
  );
}

export default function IntelligenceLayer() {
  const [activeNode, setActiveNode] = useState<string | null>(null);
  const [pulseNode, setPulseNode] = useState(0);
  const [mounted, setMounted] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    if (!mounted) return;
    const iv = setInterval(() => {
      setPulseNode(p => (p + 1) % NODES.length);
    }, 1800);
    return () => clearInterval(iv);
  }, [mounted]);

  const CX = 260, CY = 260, R = 160;

  const nodePositions = NODES.map(n => {
    const rad = (n.angle - 90) * (Math.PI / 180);
    return { ...n, x: CX + R * Math.cos(rad), y: CY + R * Math.sin(rad) };
  });

  const activeData = activeNode ? NODES.find(n => n.id === activeNode) : null;

  return (
    <section
      id="intelligence"
      className="relative py-20 overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #F6F5F0 0%, #F2EFF8 50%, #F6F5F0 100%)' }}
      aria-label="Business Intelligence Layer"
    >
      {/* Ambient */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(31,77,63,0.08) 0%, transparent 65%)', filter: 'blur(60px)' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'clamp(2rem, 4vw, 3rem)', letterSpacing: '-0.03em', color: '#0D2620', lineHeight: 1.1, marginBottom: '16px' }}>
            vina sees the whole business.
          </h2>
          <p style={{ fontFamily: 'var(--font-sans)', fontSize: '1.1rem', color: '#3D5550', maxWidth: '560px', margin: '0 auto', lineHeight: 1.65 }}>
            vina turns your business data into answers, insights, and actions - so you spend less time remembering and more time deciding.
          </p>
        </div>

        {/* Main layout */}
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* SVG Node System */}
          <div className="relative flex-shrink-0 w-full lg:w-auto" style={{ maxWidth: '520px', margin: '0 auto' }}>
            <svg
              ref={svgRef}
              viewBox="0 0 520 520"
              width="100%"
              style={{ overflow: 'visible', maxWidth: '520px' }}
              aria-label="vina intelligence network diagram"
            >
              {/* Connection lines */}
              {nodePositions.map((n) => {
                const isActive = activeNode === n.id;
                return (
                  <line
                    key={n.id}
                    x1={CX} y1={CY}
                    x2={n.x} y2={n.y}
                    stroke={isActive ? n.color : 'rgba(31,77,63,0.15)'}
                    strokeWidth={isActive ? 2 : 1}
                    strokeDasharray={isActive ? 'none' : '4 4'}
                    style={{ transition: 'all 0.4s ease' }}
                  />
                );
              })}

              {/* Animated data particles on lines */}
              {mounted && nodePositions.map((n, i) => (
                <circle key={`p-${n.id}`} r="2.5" fill={n.color} opacity="0.6">
                  <animateMotion
                    dur={`${2.5 + i * 0.4}s`}
                    repeatCount="indefinite"
                    path={`M ${CX} ${CY} L ${n.x} ${n.y}`}
                  />
                </circle>
              ))}

              {/* Center node */}
              <g>
                <circle cx={CX} cy={CY} r="48" fill="rgba(255,255,255,0.9)" stroke="rgba(31,77,63,0.15)" strokeWidth="1.5"
                  style={{ filter: 'drop-shadow(0 8px 24px rgba(31,77,63,0.12))' }} />
                <circle cx={CX} cy={CY} r="38" fill="rgba(31,77,63,0.05)" stroke="rgba(31,77,63,0.1)" strokeWidth="1" />
                <g transform={`translate(${CX - 11}, ${CY - 18})`}>
                  <ApertureMark size={22} color="#1F4D3F" />
                </g>
                <text x={CX} y={CY + 16} textAnchor="middle" fill="#16352D" fontSize="13" fontFamily="var(--font-sans)" fontWeight="500" letterSpacing="-0.5">
                  vina
                </text>
              </g>

              {/* Outer nodes */}
              {nodePositions.map((n, i) => (
                <NodeComponent
                  key={n.id}
                  node={n}
                  cx={n.x}
                  cy={n.y}
                  active={activeNode === n.id}
                  onHover={() => setActiveNode(n.id)}
                  onLeave={() => setActiveNode(null)}
                  pulse={pulseNode === i && !activeNode}
                />
              ))}
            </svg>

            <style>{`
              @keyframes nodeRing {
                0% { r: 24; opacity: 0.4; }
                100% { r: 44; opacity: 0; }
              }
            `}</style>
          </div>

          {/* Right — Insight panel */}
          <div className="flex-1 w-full lg:max-w-md">
            {activeData ? (
              <div
                key={activeData.id}
                className="rounded-3xl p-8"
                style={{
                  background: 'rgba(255,255,255,0.85)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(211,223,219,0.8)',
                  boxShadow: '0 24px 60px rgba(31,77,63,0.1)',
                  animation: 'fadeSlideUp 0.35s cubic-bezier(0.22,1,0.36,1) both',
                }}
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center"
                    style={{ background: 'rgba(31,77,63,0.08)' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: '#1F4D3F', letterSpacing: '0.04em' }}>
                      {activeData.label.slice(0, 3).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p style={{ fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: '18px', color: '#0D2620', letterSpacing: '-0.02em' }}>
                      {activeData.insight}
                    </p>
                    <p style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: '#7B8F88', letterSpacing: '0.02em' }}>
                      {activeData.sub}
                    </p>
                  </div>
                </div>
                <div className="h-px mb-6" style={{ background: 'rgba(211,223,219,0.6)' }} />
                <p style={{ fontFamily: 'var(--font-sans)', fontSize: '14px', color: '#3D5550', lineHeight: 1.7 }}>
                  {NODE_DESCRIPTIONS[activeData.id]}
                </p>
              </div>
            ) : (
              <div className="rounded-3xl p-8" style={{ background: 'rgba(255,255,255,0.5)', border: '1px dashed rgba(211,223,219,0.8)' }}>
                <p style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: '#9AADA7', letterSpacing: '0.04em', textAlign: 'center' }}>
                  Hover a node to explore live insights
                </p>
                <div className="mt-8 space-y-3">
                  {NODES.map(n => (
                    <div key={n.id} className="flex items-center gap-3 px-4 py-2.5 rounded-xl cursor-pointer transition-all"
                      style={{ background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(211,223,219,0.5)' }}
                      onMouseEnter={() => setActiveNode(n.id)}
                      onMouseLeave={() => setActiveNode(null)}
                    >
                      <div className="w-1.5 h-1.5 rounded-full" style={{ background: n.color }} />
                      <span style={{ fontFamily: 'var(--font-sans)', fontSize: '13px', color: '#4F665F', flex: 1 }}>{n.label}</span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', color: '#1F4D3F' }}>{n.insight}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
